import { REACT_APP_BACKEND_API } from '../config';
import { coalesceGet, invalidateCoalesced } from './coalesceGet';
import { invalidatePathProbe } from './pathProbeCache';
import { getHeadersApplication } from './middleware';
import { handleServiceError } from './errorHandler';

const BASE_URL = REACT_APP_BACKEND_API;

export type IntakeSessionProbe =
  | { status: 'present'; channel: ReportedSessionChannel }
  | { status: 'absent' }
  | { status: 'error'; error: Error };

export type IntakeSessionProgress = {
  lastStepId: string | null;
  completedStepIds: string[];
};

/** Channel an intake session was created through (api-client v41). */
export type SessionChannel = 'web' | 'chat';

/**
 * Channel as reported by the API. `null` = field absent (api-client without v41);
 * `'unknown'` = a value this build does not know (channel added after this deploy).
 * Fail-closed on purpose: an unreadable channel must not open the chat.
 */
export type ReportedSessionChannel = SessionChannel | 'unknown' | null;

export function toReportedChannel(raw: unknown): ReportedSessionChannel {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const value = raw.trim().toLowerCase();
  return value === 'web' || value === 'chat' ? value : 'unknown';
}

export type IntakeSession = {
  quoteId: string;
  market: string;
  schemaVersion: string;
  engineVersion: string;
  /** Capture channel (api-client v41); absent on deploys without that migration. */
  channel?: ReportedSessionChannel;
  context: Record<string, unknown>;
  answers: Record<string, unknown>;
  progress: IntakeSessionProgress;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateIntakeSessionInput = {
  quoteId: string;
  market: string;
  /** Omit to pin latest published schema for the market. */
  schemaVersion?: string;
  context: Record<string, unknown>;
};

export type PatchIntakeAnswersInput = {
  quoteId: string;
  answers?: Record<string, unknown>;
  progress?: {
    lastStepId?: string | null;
    completedStepIds?: string[];
  };
};

type ApiSuccess<T> = { message?: string; data: T };

function parseJsonSafe(response: Response): Promise<unknown> {
  return response.json().catch(() => ({}));
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body && (body as { message?: unknown }).message) {
    return String((body as { message: unknown }).message);
  }
  return fallback;
}

export function intakeSessionCacheKey(quoteId: string): string {
  return `intake-session:${quoteId.trim()}`;
}

/** Session writes and the conversation POST change the row the boot GETs just read. */
export function invalidateSessionReads(quoteId: string): void {
  const id = quoteId.trim();
  if (!id) return;
  invalidateCoalesced(intakeSessionCacheKey(id));
  invalidateCoalesced(`application:${id}`);
  invalidatePathProbe(id);
}

type SessionRead =
  | { kind: 'ok'; session: IntakeSession }
  | { kind: 'absent' }
  | { kind: 'error'; error: Error };

/**
 * One GET for the probe and the full read. Both hit
 * `GET /intake/sessions/:quoteId`; sharing the response is what keeps the chat
 * boot from issuing that call once per caller.
 */
function readIntakeSession(quoteId: string): Promise<SessionRead> {
  const id = quoteId.trim();
  return coalesceGet(
    intakeSessionCacheKey(id),
    async () => {
      const response = await fetch(`${BASE_URL}intake/sessions/${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: getHeadersApplication(),
      });
      if (response.status === 404) return { kind: 'absent' } as const;
      if (!response.ok) {
        const body = await parseJsonSafe(response);
        return {
          kind: 'error' as const,
          error: new Error(messageFromBody(body, `Intake session read failed: HTTP ${response.status}`)),
        };
      }
      const payload = (await response.json()) as ApiSuccess<IntakeSession>;
      return { kind: 'ok' as const, session: normalizeSession(payload.data) };
    },
    // In-flight only. A stored body would hide answers the chat just wrote:
    // the step viewer refetches this GET on every turn.
    { ttlMs: 0, cacheIf: (read) => read.kind !== 'error' },
  );
}

/**
 * GET /intake/sessions/:quoteId — W5a sticky probe.
 * 404 → absent; 2xx → present; other → error (caller must retry, not guess path).
 * `fresh` drops an in-flight GET so a retry does not join a read that started earlier.
 */
export async function probeIntakeSession(
  quoteId: string,
  options?: { fresh?: boolean },
): Promise<IntakeSessionProbe> {
  if (options?.fresh) invalidateCoalesced(intakeSessionCacheKey(quoteId));
  try {
    const read = await readIntakeSession(quoteId);
    if (read.kind === 'absent') return { status: 'absent' };
    if (read.kind === 'error') return { status: 'error', error: read.error };
    return { status: 'present', channel: read.session.channel ?? null };
  } catch (error) {
    handleServiceError(error);
    return {
      status: 'error',
      error: error instanceof Error ? error : new Error('Intake session probe failed'),
    };
  }
}

/** GET /intake/sessions/:quoteId — full resume payload. */
export async function getIntakeSession(quoteId: string): Promise<IntakeSession | null> {
  try {
    const read = await readIntakeSession(quoteId);
    if (read.kind === 'absent') return null;
    if (read.kind === 'error') throw read.error;
    return read.session;
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
}

/**
 * POST /intake/sessions — create or return existing (idempotent).
 * 409 = legacy quote_form sticky without A2 session.
 */
export async function createIntakeSession(
  input: CreateIntakeSessionInput,
): Promise<IntakeSession> {
  invalidateSessionReads(input.quoteId);
  try {
    const body: Record<string, unknown> = {
      quoteId: input.quoteId,
      market: input.market,
      context: input.context,
    };
    if (input.schemaVersion) {
      body.schemaVersion = input.schemaVersion;
    }

    const response = await fetch(`${BASE_URL}intake/sessions`, {
      method: 'POST',
      headers: getHeadersApplication(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errBody = await parseJsonSafe(response);
      const err = new Error(
        messageFromBody(errBody, `Failed to create intake session: HTTP ${response.status}`),
      ) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    const payload = (await response.json()) as ApiSuccess<IntakeSession>;
    return normalizeSession(payload.data);
  } catch (error) {
    handleServiceError(error);
    throw error;
  } finally {
    invalidateSessionReads(input.quoteId);
  }
}

export type IntakeSubmitDecision = {
  attempt: number;
  outcome: string;
  mode: string;
  pipelineVersion: string;
  asaRulesVersion?: string;
  schemaVersion: string;
  channel: string;
  inputsHash: string;
};

/** Hito de proceso pendiente (IC2/A31): p.ej. la firma en MX. */
export type IntakeSubmitPendingStep = {
  id: string;
  type?: string;
};

export type IntakeSubmitResult = {
  quoteId: string;
  /**
   * IC2 (A30/A31): el submit VALIDA y DERIVA — complete=true significa que el
   * checklist del mercado quedó completo y la solicitud entró a evaluación
   * (INT: en el Accept). complete=false = "validado, faltan hitos"
   * (MX: la firma) — pendingSteps los enumera.
   */
  complete: boolean;
  pendingSteps: IntakeSubmitPendingStep[];
};

export type IntakeSubmitSchemaIssue = {
  path: string;
  message: string;
};

/**
 * POST /intake/sessions/:quoteId/submit — IC2: validar + derivar (A30/A31).
 * 422 = answers failed schema validation (blocks Continuar).
 * 200 = validado; complete dice si la derivación disparó el envío.
 */
export async function submitIntakeSession(quoteId: string): Promise<IntakeSubmitResult> {
  invalidateSessionReads(quoteId);
  try {
    const response = await fetch(
      `${BASE_URL}intake/sessions/${encodeURIComponent(quoteId)}/submit`,
      {
        method: 'POST',
        headers: getHeadersApplication(),
      },
    );

    if (response.status === 422) {
      const errBody = (await parseJsonSafe(response)) as {
        message?: string;
        issues?: IntakeSubmitSchemaIssue[];
      };
      const err = new Error(
        messageFromBody(errBody, 'Answers failed schema validation'),
      ) as Error & { status?: number; issues?: IntakeSubmitSchemaIssue[] };
      err.status = 422;
      err.issues = Array.isArray(errBody?.issues) ? errBody.issues : [];
      throw err;
    }

    if (!response.ok) {
      const errBody = await parseJsonSafe(response);
      throw new Error(
        messageFromBody(errBody, `Failed to submit intake session: HTTP ${response.status}`),
      );
    }

    const payload = (await response.json()) as ApiSuccess<IntakeSubmitResult>;
    const data = payload.data ?? { quoteId, complete: false, pendingSteps: [] };
    return {
      quoteId: data.quoteId ?? quoteId,
      complete: Boolean(data.complete),
      pendingSteps: Array.isArray(data.pendingSteps) ? data.pendingSteps : [],
    };
  } catch (error) {
    handleServiceError(error);
    throw error;
  } finally {
    invalidateSessionReads(quoteId);
  }
}

/** PATCH /intake/sessions/:quoteId/answers — deep-merge answers (+ optional progress). */
export async function patchIntakeAnswers(
  input: PatchIntakeAnswersInput,
): Promise<IntakeSession> {
  invalidateSessionReads(input.quoteId);
  try {
    const body: Record<string, unknown> = {
      answers: input.answers,
    };
    if (input.progress) {
      body.progress = {
        lastStepId: input.progress.lastStepId,
        completedStepIds: input.progress.completedStepIds,
      };
    }

    const response = await fetch(
      `${BASE_URL}intake/sessions/${encodeURIComponent(input.quoteId)}/answers`,
      {
        method: 'PATCH',
        headers: getHeadersApplication(),
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const errBody = await parseJsonSafe(response);
      throw new Error(
        messageFromBody(errBody, `Failed to patch intake answers: HTTP ${response.status}`),
      );
    }

    const payload = (await response.json()) as ApiSuccess<IntakeSession>;
    return normalizeSession(payload.data);
  } catch (error) {
    handleServiceError(error);
    throw error;
  } finally {
    invalidateSessionReads(input.quoteId);
  }
}

/** In-flight ensure by quoteId — collapses React StrictMode / double-mount races. */
const ensureIntakeSessionInflight = new Map<string, Promise<IntakeSession>>();

/**
 * Ensure a session exists for the new path: GET first, else create (idempotent).
 * Concurrent callers for the same quoteId share one network round-trip.
 */
export async function ensureIntakeSession(
  input: CreateIntakeSessionInput,
): Promise<IntakeSession> {
  const key = input.quoteId.trim();
  const pending = ensureIntakeSessionInflight.get(key);
  if (pending) {
    return pending;
  }

  const run = (async () => {
    const existing = await getIntakeSession(key);
    if (existing) {
      return existing;
    }
    return createIntakeSession({ ...input, quoteId: key });
  })().finally(() => {
    ensureIntakeSessionInflight.delete(key);
  });

  ensureIntakeSessionInflight.set(key, run);
  return run;
}

export type GenerateIntakeSummaryPdfResult = {
  path: string;
  uploaded: boolean;
};

/**
 * POST /intake/sessions/:quoteId/summary.pdf — server-side MX-SUMMARY-PDF.
 * Coverage comes from ole-api-quotation on the server; the browser does not send quote.
 */
export async function generateIntakeSummaryPdf(
  quoteId: string,
  input?: { accepted?: boolean },
): Promise<GenerateIntakeSummaryPdfResult> {
  invalidateSessionReads(quoteId);
  try {
    const response = await fetch(
      `${BASE_URL}intake/sessions/${encodeURIComponent(quoteId)}/summary.pdf`,
      {
        method: 'POST',
        headers: getHeadersApplication(),
        body: JSON.stringify({
          accepted: Boolean(input?.accepted),
        }),
      },
    );
    if (!response.ok) {
      const errBody = await parseJsonSafe(response);
      throw new Error(
        messageFromBody(errBody, `Failed to generate summary PDF: HTTP ${response.status}`),
      );
    }
    const payload = (await response.json()) as ApiSuccess<GenerateIntakeSummaryPdfResult>;
    return {
      path: payload.data?.path ?? '',
      uploaded: Boolean(payload.data?.uploaded),
    };
  } catch (error) {
    handleServiceError(error);
    throw error;
  } finally {
    invalidateSessionReads(quoteId);
  }
}

/**
 * GET /intake/sessions/:quoteId/summary.pdf — download the stored (or on-the-fly) PDF.
 */
export async function downloadIntakeSummaryPdf(quoteId: string): Promise<void> {
  try {
    const response = await fetch(
      `${BASE_URL}intake/sessions/${encodeURIComponent(quoteId)}/summary.pdf`,
      {
        method: 'GET',
        headers: getHeadersApplication(),
      },
    );
    if (!response.ok) {
      const errBody = await parseJsonSafe(response);
      throw new Error(
        messageFromBody(errBody, `Failed to download summary PDF: HTTP ${response.status}`),
      );
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'summary.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
}

function normalizeSession(raw: IntakeSession): IntakeSession {
  const context =
    raw?.context && typeof raw.context === 'object' && !Array.isArray(raw.context)
      ? (raw.context as Record<string, unknown>)
      : {};
  const answers =
    raw?.answers && typeof raw.answers === 'object' && !Array.isArray(raw.answers)
      ? (raw.answers as Record<string, unknown>)
      : {};
  const progress = raw?.progress ?? { lastStepId: null, completedStepIds: [] };

  return {
    ...raw,
    channel: toReportedChannel(raw?.channel),
    context,
    answers,
    progress: {
      lastStepId: progress.lastStepId ?? null,
      completedStepIds: Array.isArray(progress.completedStepIds)
        ? progress.completedStepIds
        : [],
    },
  };
}
